import type { AbstractControl, NonNullableFormBuilder, ValidationErrors } from '@angular/forms';
import { Validators } from '@angular/forms';
import { type ControlInterface, selectControlValue } from '@comparit/core';
import { combineLatest, distinctUntilChanged, map, shareReplay, tap } from 'rxjs';
import type { Step5FormInput } from '../../../interfaces';

/**
 * The living area inside the Keller is part of it, so it cannot be larger than the Keller.
 * Reports the plain `max` shape `Validators.max` uses, so the generic numeric message covers it.
 */
function wohnflaecheWithinKellerValidator(control: AbstractControl): ValidationErrors | null {
  const gesamtflaeche = control.parent?.get('gesamtflaecheImKeller')?.value;
  // Until the Gesamtfläche is a usable number there is nothing to bound against, and its own `min`
  // error already says so — reporting a maximum of 0 here would blame the wrong field.
  if (typeof gesamtflaeche !== 'number' || gesamtflaeche <= 0) return null;
  if (typeof control.value !== 'number') return null;

  return control.value > gesamtflaeche
    ? { max: { max: gesamtflaeche, actual: control.value } }
    : null;
}

/**
 * The Keller sits under the home, so it cannot cover more ground than the Wohnfläche above it.
 * Reports the plain `max` shape `Validators.max` uses, so the generic numeric message covers it.
 */
function gesamtflaecheWithinWohnflaecheValidator(
  control: AbstractControl
): ValidationErrors | null {
  const wohnflaeche = control.parent?.get('wohnflaecheOhneKeller')?.value;
  // Until the Wohnfläche is a usable number there is nothing to bound against, and its own `min`
  // error already says so — reporting a maximum of 0 here would blame the wrong field.
  if (typeof wohnflaeche !== 'number' || wohnflaeche <= 0) return null;
  if (typeof control.value !== 'number') return null;

  return control.value > wohnflaeche ? { max: { max: wohnflaeche, actual: control.value } } : null;
}

export class Step5Form {
  constructor(
    private fb: NonNullableFormBuilder,
    private defaultValue?: Step5FormInput | null
  ) {
    this.kellerValidation$.subscribe();
    this.kellerFlaechenCrossCheck$.subscribe();
    this.wohnflaecheCrossCheck$.subscribe();
  }

  ctrls: ControlInterface<Step5FormInput> = {
    // The Sparte takes a Wohnfläche of 1 to 1000 m².
    wohnflaecheOhneKeller: this.fb.control<number>(this.defaultValue?.wohnflaecheOhneKeller ?? 0, [
      Validators.required,
      Validators.min(1),
      Validators.max(1000),
    ]),
    // Checked and disabled, exactly as the B2B calculator ships it: the Sparte only prices
    // permanently occupied homes today, so the funnel shows the assumption rather than asking.
    // A restored value is ignored on purpose — there is no other value it could take.
    staendigBewohnt: this.fb.control<boolean>({ value: true, disabled: true }),
    kellerVorhanden: this.fb.control<boolean>(this.defaultValue?.kellerVorhanden ?? false),
    gesamtflaecheImKeller: this.fb.control<number>(this.defaultValue?.gesamtflaecheImKeller ?? 0),
    wohnflaecheImKeller: this.fb.control<number>(this.defaultValue?.wohnflaecheImKeller ?? 0),
  };

  public kellerVorhanden$ = selectControlValue(this.ctrls.kellerVorhanden);

  /**
   * The Wohnfläche the Versicherungssumme is measured against. The Keller only counts once the
   * customer says there is one — its control keeps the last entered value after they untick it.
   */
  public wohnflaecheGesamt$ = combineLatest([
    selectControlValue(this.ctrls.wohnflaecheOhneKeller),
    selectControlValue(this.ctrls.wohnflaecheImKeller),
    this.kellerVorhanden$,
  ]).pipe(
    map(
      ([wohnflaeche, imKeller, kellerVorhanden]) =>
        (wohnflaeche ?? 0) + (kellerVorhanden ? (imKeller ?? 0) : 0)
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** Both Keller areas are Pflicht as soon as there is a Keller, and meaningless without one. */
  private kellerValidation$ = this.kellerVorhanden$.pipe(
    tap(kellerVorhanden => {
      // A Keller has an area; only its *living* area may legitimately be 0, which is the
      // ordinary case of a Keller used purely for storage.
      this.ctrls.gesamtflaecheImKeller.setValidators(
        kellerVorhanden
          ? [Validators.required, Validators.min(1), gesamtflaecheWithinWohnflaecheValidator]
          : []
      );
      this.ctrls.wohnflaecheImKeller.setValidators(
        kellerVorhanden
          ? [Validators.required, Validators.min(0), wohnflaecheWithinKellerValidator]
          : []
      );

      this.ctrls.gesamtflaecheImKeller.updateValueAndValidity();
      this.ctrls.wohnflaecheImKeller.updateValueAndValidity();
    })
  );

  /** The cross-check hangs off the Wohnfläche, so a Gesamtfläche edit has to re-run it. */
  private kellerFlaechenCrossCheck$ = selectControlValue(this.ctrls.gesamtflaecheImKeller).pipe(
    tap(() => this.ctrls.wohnflaecheImKeller.updateValueAndValidity({ emitEvent: false }))
  );

  /** Likewise the Gesamtfläche is bounded from above, so shrinking the Wohnfläche has to re-run it. */
  private wohnflaecheCrossCheck$ = selectControlValue(this.ctrls.wohnflaecheOhneKeller).pipe(
    tap(() => this.ctrls.gesamtflaecheImKeller.updateValueAndValidity({ emitEvent: false }))
  );

  public fg = this.fb.group(this.ctrls);
}
